import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { UserService } from '@core/services/auth/user.service';
import { ApplicationVersionService } from '@core/services/application-version.service';
import { DatadogService } from '@core/services/datadog.service';
import { PushNotificationService } from '@core/services/push-notification.service';
import { environment } from '@environments/environment';
import { initializeApp } from '@firebase/app';
import { TranslateService } from '@ngx-translate/core';
import { TegelModule } from '@scania/tegel-angular-17';
import { NotificationPopupComponent } from '@shared/components/notification-popup/notification-popup.component';
import { StencilNavbarComponent } from '@shared/components/stencil-navbar/stencil-navbar.component';
import { StencilSideMenuComponent } from '@shared/components/stencil-sidemenu/stencil-side-menu.component';
import { ZendeskComponent } from '@shared/components/zendesk/zendesk.component';
import { ApplicationUpdateToastService } from '@shared/services/application-update-toast.service';
import { CookieConsentService } from '@shared/services/cookieConsent.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { SharedService } from '@shared/services/shared.service';
import { MessagePayload } from 'firebase/messaging';
import { AnnouncementBannerComponent } from '@shared/components/announcement-banner/announcement-banner.component';
import { take } from 'rxjs/operators';

initializeApp(environment.firebase);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterModule,
    StencilNavbarComponent,
    StencilSideMenuComponent,
    NotificationPopupComponent,
    ZendeskComponent,
    CommonModule,
    TegelModule,
    AnnouncementBannerComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private sharedService = inject(SharedService);
  private translate = inject(TranslateService);
  private dataDog = inject(DatadogService);
  private cookieConsentService = inject(CookieConsentService);

  userService = inject(UserService);
  router = inject(Router);
  launchDarklyService = inject(LaunchDarklyService);
  private pushNotificationService = inject(PushNotificationService);
  private readonly versionService = inject(ApplicationVersionService);
  private readonly applicationUpdateToastService = inject(ApplicationUpdateToastService);

  languageCode = this.sharedService.languageCode;

  uiMode = this.sharedService.modeSignal;
  uiVariant = this.sharedService.modeVariantSignal;
  showNotificationPopup = signal<boolean>(false);
  message = computed<MessagePayload | null>(() => this.pushNotificationService.messageSignal());

  version = environment.appVersion;

  sideMenuStateChanged(expanded: boolean) {
    this.dataDog.sidemenuChanged(expanded);
  }

  constructor() {
    effect((onCleanup) => {
      const lang = this.languageCode();

      if (lang.length === 0) return;

      const subscription = this.translate
        .use(lang)
        .pipe(take(1))
        .subscribe(() => {
          this.versionService.start();
          this.applicationUpdateToastService.start();
        });

      this.cookieConsentService.loadCookieConsentScript(lang);
      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => {
      const newMessage = this.message();
      if (newMessage) {
        this.showNotificationPopup.set(true);
      }
    });
  }

  ngOnInit() {
    console.log(
      `%c charging-services version ${this.version}`,
      ' background: linear-gradient(to right, #2B70D3, #000000)',
    );
    this.userService.initializeUser();
    this.pushNotificationService.requestPermission();
    this.pushNotificationService.listen();
  }

  unlimitedWidthRoutes(): boolean {
    return ['/overview', '/map'].some((route) => this.router.url.includes(route));
  }

  primaryModeVariantRoutes(): boolean {
    return ['/departure-scheduling'].some((route) => this.router.url.includes(route));
  }

  modeVariantClass(): string {
    if (this.primaryModeVariantRoutes()) {
      return 'tds-mode-variant-primary';
    }
    return this.uiVariant();
  }

  isMapRoute(): boolean {
    return this.router.url.includes('/map');
  }

  closePopup() {
    this.showNotificationPopup.set(false);
  }
}
